import { logger } from '@adornis/base/logging.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { defineMailTemplate } from '@adornis/mails/db/mail-template-definition.js';
import { generateLoginToken } from '@adornis/users/server/token.js';
import { ComponentParamaterType, ComponentType, MessageType } from '@adornis/whatsapp/server/types.js';
import { bhBaseQlContext } from '../bh-context.js';
import { ColorDefinitions } from '../entities/ColorDefinitions.js';
import { Institution } from '../entities/institution.js';
import {
  getWhatsappTemplate,
  InstitutionMailTemplate,
  MailTemplateType,
  MailTemplateVars,
} from '../entities/mail-templates.js';
import { Participation } from '../entities/Participation.js';
import { SettingColorDefintionProp } from '../entities/settings.js';
import { User } from '../entities/user.js';
import { sendInstitutionEmail } from '../mails/sendInstitutionMail.js';
import { getNotificationType, sendWhatsapp } from './_helper.js';

defineMailTemplate(MailTemplateType.SPERRUNG_AUFGEHOBEN, {
  getMailData: arg1 => ({ data: {}, attachments: {}, scope: 'default' }),
  defaultTemplate: {
    subject: 'Sperrung durch Veranstalter aufgehoben',
    content: `
          <p>Hallo {{title}} {{firstname}} {{lastname}},</p>
<p>wir haben Ihre Sperrung aufgehoben und freuen uns, dass sich die Dinge, die zu Ihrer Sperrung f&uuml;hrten, aufgekl&auml;rt haben. Vielen Dank f&uuml;r Ihre Kontaktaufnahme.</p>
<p>Viele Gr&uuml;&szlig;e</p>
<p>---------------------------------------------------------------------</p>
<p>Hello {{title}} {{firstname}} {{lastname}},</p>
<p>We have successfully unblocked your account and are pleased that the issues that led to your blocking have been resolved. Thank you for contacting us.&nbsp;</p>
<p>Best regards</p>
        `,
    footer: '',
  },
  variables: InstitutionMailTemplate.getAvailableVariables(MailTemplateType.SPERRUNG_AUFGEHOBEN),
  attachments: [],
  displayName: 'Sperrung aufgehoben',
  editorHint: () => InstitutionMailTemplate.getMailHint(MailTemplateType.SPERRUNG_AUFGEHOBEN),
});

export async function notifyNuzterSperrungAufgehoben(participationID: string) {
  const participation = await getByID(
    Participation,
    participationID,
  )({
    _id: 1,
    _class: 1,
    userID: 1,
    productID: 1,
    state: 1,
    waitingListPlace: 1,
    product: {
      _id: 1,
      name: 1,
      institutionID: 1,
      appointments: { start: 1, name: 1, end: 1, location: 1, meetingLink: { link: 1 } },
      description: 1,
      translatedDescriptionHTML: { _class: 1, langKey: 1, text: 1 },
      sortingWeight: 1,
      hasPrice: 1,
      categoryID: 1,
      timeframeID: 1,
      attachedCertificate: { _class: 1, certificateID: 1, exportDate: 1 },
      lastReminderMailSent: 1,
      publishDate: 1,
      topic: 1,
      language: 1,
      presenceType: 1,
      isCanceled: 1,
      capacity: 1,
      hideCapacity: 1,
      slots: { _id: 1, start: 1, end: 1 },
      settings: {
        blockAutomaticMoveUpDaysInAdvance: 1,
        inheritedBlockAutomaticMoveUpDaysInAdvance: 1,
        blockSignOffDaysInAdvance: 1,
        inheritedBlockSignOffDaysInAdvance: 1,
        courseLocation: 1,
        inheritedCourseLocation: 1,
      },
    },
    user: { _id: 1, title: 1, firstName: 1, lastName: 1, email: 1, whatsappNotification: 1, whatsappPhone: 1 },
  });
  if (!participation) throw new Error('Couldnt find participation with ID ' + participationID);
  const waitingListPlace = participation.waitingListPlace;

  if (!participation.product) throw new Error('product not found');

  const institution = await getByID(
    Institution,
    participation.product.institutionID,
  )({
    _id: 1,
    name: 1,
    colorDefintions: selectionSet(() => ColorDefinitions, 4),
    settings: {
      colorDefinitions: selectionSet(() => ColorDefinitions, 4),
      inheritedColorDefinitions: selectionSet(() => SettingColorDefintionProp, 4),
    },
  });
  if (!institution) throw new Error('institution not found ' + participation.product.institutionID);

  const user = participation.user;
  if (!user) throw new Error('no user is set');

  const token = await generateLoginToken({
    userID: user._id,
    userClass: User._class,
    ttl: 1000 * 60 * 60 * 24 * 7 /* 7 days */,
  });

  const institutionID = participation.product?.institutionID;

  // Set mail theme
  bhBaseQlContext.colorDefinitions =
    institution?.settings?.colorDefinitions ?? institution?.settings?.inheritedColorDefinitions?.value;

  const mailTemplate = await InstitutionMailTemplate.getMailTemplateForInstitution(
    institutionID,
    MailTemplateType.SPERRUNG_AUFGEHOBEN,
  )(InstitutionMailTemplate.allFields);

  const notificationType = getNotificationType(user, mailTemplate);
  if (notificationType === 'whatsapp') {
    await sendWhatsapp({
      to: user.whatsappPhone,
      type: MessageType.TEMPLATE,
      content: {
        template: {
          name: getWhatsappTemplate(MailTemplateType.SPERRUNG_AUFGEHOBEN).name,
          language: {
            code: 'de',
          },
          components: [
            {
              type: ComponentType.BODY,
              parameters: [
                {
                  type: ComponentParamaterType.TEXT,
                  text: InstitutionMailTemplate.mapFirstNameVar(user) || '-',
                },
                {
                  type: ComponentParamaterType.TEXT,
                  text: InstitutionMailTemplate.mapLastNameVar(user) || '-',
                },
              ],
            },
          ],
        },
      },
    });
  } else if (notificationType === 'email') {
    const mailVars = {
      [MailTemplateVars.TITLE]: participation.user?.title ?? '',
      [MailTemplateVars.FIRST_NAME]: participation.user?.firstName,
      [MailTemplateVars.LAST_NAME]: participation.user?.lastName,
      [MailTemplateVars.EVENT_TITLE]: participation.product?.name,
      [MailTemplateVars.LOGIN_LINK]: InstitutionMailTemplate.mapLoginLinkVar(token),
      [MailTemplateVars.CHANGED_REGISTRATIONS]: [
        InstitutionMailTemplate.mapChangedRegistrationVar({
          productBefore: participation.product,
          newState: participation.state,
          newWaitingListPlace: waitingListPlace,
          participation,
          institution,
          englishVersion: false,
        }),
      ],
      [MailTemplateVars.CHANGED_REGISTRATIONS_EN]: [
        InstitutionMailTemplate.mapChangedRegistrationVar({
          productBefore: participation.product,
          newState: participation.state,
          newWaitingListPlace: waitingListPlace,
          participation,
          institution,
          englishVersion: true,
        }),
      ],
    };

    const content = await mailTemplate.buildEmail(mailVars as any);
    const subject = await mailTemplate.buildSubject(mailVars as any);

    if (!content) throw new Error('mail template has no content!');
    if (!subject) throw new Error('mail template has no subject!');

    await sendInstitutionEmail({
      email: user.email,
      institutionID,
      content,
      subject,
      attachmentPaths: [] as string[],
    });
  } else {
    // no notification send because both are deactivated
    logger.warn(
      { notificationType, notification: mailTemplate.emailType },
      'no notification send because neither email nor whatsapp are activated',
    );
  }
}
